import React, { useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Box } from '@mui/material';
import OverflowingTooltipText from 'components/OverflowingTooltipText';
import routeEndpoints from 'route/endpoints';
import { getThemeEditorPath } from 'views/V2/Medallion/helper/constants';
import { themesFromApi } from 'views/V2/Medallion/helper/utilities';

import { useFetchAllThemesQuery } from 'redux/slices/themesApi';

import styles from '../sidebar.module.css';

import { getActiveItemId, sortDefaultFirst } from '../helper/utilities';

function ThemesSubNav() {
    const { pathname } = useLocation();
    const scrollRef = useRef(null);
    const { data, isLoading, isError, isFetching, refetch } = useFetchAllThemesQuery();
    const themes = useMemo(() => sortDefaultFirst(themesFromApi(data), 'isDefault'), [data]);

    const listedPaths = themes.map((theme) => getThemeEditorPath(theme.nanoid));
    const onThemes = getActiveItemId(pathname) === 'themes';
    const activePath = listedPaths.includes(pathname) ? pathname : routeEndpoints.themes;

    // Keep the open theme visible when it sits below the fold of the list.
    useEffect(() => {
        scrollRef.current
            ?.querySelector('[aria-current="page"]')
            ?.scrollIntoView({ block: 'nearest' });
    }, [activePath, themes]);

    const linkProps = (path) => ({
        to: path,
        isActive: () => onThemes && path === activePath,
        className: styles.subNavItem,
        activeClassName: styles.subNavItemActive,
    });

    return (
        <Box className={styles.subNav}>
            <NavLink {...linkProps(routeEndpoints.themes)}>All themes</NavLink>
            {isLoading && (
                <Box className={styles.subNavStatus} role="status" aria-label="Loading themes">
                    <span className={styles.subNavSkeleton} />
                    <span className={styles.subNavSkeleton} />
                    <span className={styles.subNavSkeleton} />
                </Box>
            )}
            {isError && !isLoading && (
                <Box className={styles.subNavError}>
                    <span>Couldn't load themes</span>
                    <button
                        type="button"
                        className={styles.retryButton}
                        onClick={refetch}
                        disabled={isFetching}
                    >
                        Retry
                    </button>
                </Box>
            )}
            {themes.length > 0 && (
                <Box ref={scrollRef} className={styles.subNavScroll}>
                    {themes.map((theme) => (
                        <NavLink
                            key={theme.nanoid}
                            {...linkProps(getThemeEditorPath(theme.nanoid))}
                        >
                            <OverflowingTooltipText
                                title={theme.name}
                                className={styles.subNavLabel}
                                placement="right"
                            />
                            {theme.isDefault && <span className={styles.defaultTag}>Default</span>}
                        </NavLink>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default ThemesSubNav;
